import React from 'react'
import { useFormContext, get } from 'react-hook-form'
import type { FieldPath, FieldValues } from 'react-hook-form'

interface FormFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>
  label: string
  type?: 'text' | 'number' | 'email'
  placeholder?: string
  /**
   * Optional input attributes forwarded to the underlying element.
   * Keep these small; complex inputs should get their own component.
   */
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  autoComplete?: string
  step?: string | number
}

/**
 * Bound text/number input wired through `react-hook-form`'s `useFormContext`.
 * Reading from `errors` via `get()` handles nested paths like
 * `debts.0.currentBalance` without complaint.
 *
 * @returns JSX.Element The labeled input with inline error rendering
 */
export function FormField<TFieldValues extends FieldValues>({
  name,
  label,
  type = 'text',
  placeholder,
  inputMode,
  autoComplete,
  step,
}: FormFieldProps<TFieldValues>): JSX.Element {
  const {
    register,
    formState: { errors },
  } = useFormContext<TFieldValues>()
  const error = get(errors, name) as { message?: string } | undefined
  const errorId = error ? `${name}-error` : undefined

  return (
    <div className="form-group">
      <label htmlFor={name}>{label}</label>
      <input
        id={name}
        type={type}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        step={step}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={errorId}
        {...register(name)}
      />
      {error?.message && (
        <p id={errorId} className="field-error" role="alert">
          {String(error.message)}
        </p>
      )}
    </div>
  )
}
